"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, SlidersHorizontal as Sliders, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
interface ProductCardData {
  id: string;
  name: string;
  subtitle: string;
  tag: string;
  link: string;
  image: string;
  srcset: string;
  sizes: string;
  description: string;
  keyFeature: string;
}
const familyProducts: ProductCardData[] = [{
  id: "bruker-beam",
  name: "Bruker BEAM",
  subtitle: "Compact Solid-State NIR",
  tag: "Non-Contact NIR",
  link: "/UserBeamPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_beam_analyzer.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_beam_analyzer.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/bruker_beam_analyzer.png-wvc-sizes",
  description: "Real-time non-destructive NIR analysis designed for seamless integration over conveyor belts and pipelines.",
  keyFeature: "High-speed continuous monitoring"
}, {
  id: "matrix-f-ii",
  name: "MATRIX-F II",
  subtitle: "FT-NIR Process Spectrometer",
  tag: "Multiplexed FT-NIR",
  link: "/MatrixFIIPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/matrix_f_ii_spectrometer.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/matrix_f_ii_spectrometer.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/matrix_f_ii_spectrometer.png-wvc-sizes",
  description: "Industry-standard FT-NIR spectrometer offering up to 6 fiber-optic measurement channels for solid and liquid streams.",
  keyFeature: "Direct fiber-optic multiplexing"
}, {
  id: "hyperflux-pro-plus",
  name: "HyperFlux PRO Plus",
  subtitle: "High-Throughput Process Raman",
  tag: "High Sensitivity Raman",
  link: "/HyperfluxProPlusPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hyperflux_pro_plus_raman.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hyperflux_pro_plus_raman.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/hyperflux_pro_plus_raman.png-wvc-sizes",
  description: "Patented High-Throughput Virtual Slit (HTVS) technology providing unparalleled optical throughput and precision.",
  keyFeature: "HTVS optical advantage"
}, {
  id: "process-guardian",
  name: "Process Guardian",
  subtitle: "Continuous Raman Workstation",
  tag: "Process Raman",
  link: "/ProcessGuardianPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_guardian_station.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_guardian_station.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/process_guardian_station.png-wvc-sizes",
  description: "Purpose-built Raman analyzer engineered for demanding ATEX-rated manufacturing and hazardous production environments.",
  keyFeature: "Hazardous zone ready"
}, {
  id: "superflux",
  name: "SuperFlux",
  subtitle: "Versatile Raman Platform",
  tag: "Flexible PAT",
  link: "/SuperFluxPage",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/superflux_system_unit.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/superflux_system_unit.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/superflux_system_unit.png-wvc-sizes",
  description: "Next-generation compact Raman platform delivering benchtop performance in a ruggedized, field-ready footprint.",
  keyFeature: "Modular sampling optics"
}];
const wp_query_params = {
  per_page: 5,
  order: "asc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function ProductFamilyConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  return <div className="space-y-12">
      {error && <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-center text-sm text-destructive flex items-center justify-between">
          <span>Failed to sync store catalog. Displaying offline product directory.</span>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Retry
          </Button>
        </div>}

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {familyProducts.map((item, i) => {
        const matchedLiveProduct = products?.find(p => p.slug.toLowerCase() === item.id || p.title.toLowerCase().includes(item.name.toLowerCase()));
        return <Card key={item.id} data-index={i} className="flex flex-col justify-between border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden group">
              <div>
                <div className="relative border-b border-border bg-muted/40 p-6 flex items-center justify-center">
                  <div className="absolute top-4 left-4 z-10">
                    <Badge variant="secondary" className="font-semibold text-xs bg-secondary text-secondary-foreground border-border">
                      {item.tag}
                    </Badge>
                  </div>
                  <div className="w-full max-w-[260px]">
                    <AspectRatio ratio={4 / 3} className="flex items-center justify-center">
                      <img src={matchedLiveProduct?.featured_image_url || item.image} data-wvc-srcset={matchedLiveProduct ? undefined : item.srcset} data-wvc-sizes={matchedLiveProduct ? undefined : item.sizes} alt={matchedLiveProduct?.title || item.name} loading="lazy" decoding="async" className="h-full w-full object-contain p-2 transition-transform duration-300 group-hover:scale-105" />
                    </AspectRatio>
                  </div>
                </div>

                <CardHeader className="space-y-2 pt-6">
                  <div className="text-xs font-mono font-medium tracking-wide text-primary uppercase">
                    {item.subtitle}
                  </div>
                  <CardTitle className="text-xl font-bold tracking-tight text-card-foreground">
                    <Link to={item.link} className="hover:text-primary transition-colors">
                      {item.name}
                    </Link>
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                    {item.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4 pt-2">
                  <div className="flex items-center gap-2 text-xs font-medium text-foreground bg-accent/40 rounded-md p-2.5 border border-border">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span className="truncate">{item.keyFeature}</span>
                  </div>

                  {matchedLiveProduct && <div className="pt-2">
                      <ProductPrice product={matchedLiveProduct} className="text-sm font-semibold text-foreground" />
                    </div>}
                </CardContent>
              </div>

              <CardFooter className="flex flex-col gap-3 pt-4 border-t border-border bg-muted/10">
                {matchedLiveProduct ? <div className="w-full">
                    <AddToCartButton product={matchedLiveProduct} productId={matchedLiveProduct.id} variant="default" className="w-full" buttonClassName="bg-primary text-foreground-primary w-full shadow-xs" text="Configure Unit" showQuantity={false} />
                  </div> : null}

                <Button asChild variant="outline" className="w-full justify-between group-hover:border-primary/50">
                  <Link to={item.link}>
                    <span>View Specifications</span>
                    <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>;
      })}
      </div>
    </div>;
}
export default function FamilyShowcase() {
  return <section id="productfamilyshowcase" data-nav="light" className="bg-background py-20 lg:py-28">
      <div className="page-container space-y-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-border pb-8">
          <div className="max-w-2xl space-y-3">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-medium">
              <Sliders className="size-3.5 text-primary" />
              <span>Process Analytical Technology</span>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Bruker PAT Product Family
            </h2>
            <p className="text-base text-muted-foreground leading-relaxed">
              Engineered for continuous monitoring, raw material identification, and real-time release testing across regulated biopharmaceutical and chemical operations.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button asChild variant="outline">
              <Link to="/ProcessAnalyticalTechnologyPage">
                Learn About PAT
              </Link>
            </Button>
            <Button asChild variant="default">
              <Link to="/BookADemoPage">
                Request a Demo
              </Link>
            </Button>
          </div>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 5,
        order: "asc",
        orderby: "date"
      }} className="w-full">
          <ProductFamilyConsumer />
        </EcommerceProductsProvider>

        <div className="rounded-xl border border-border bg-secondary/30 p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <ShieldCheck className="size-6" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-foreground">Complete Qualification & Lifecycle Support</h3>
              <p className="text-sm text-muted-foreground">
                All Bruker PAT systems are supplied with full validation protocols, IQ/OQ execution, and local service across Ireland and the UK.
              </p>
            </div>
          </div>
          <Button asChild variant="secondary" className="shrink-0 bg-background hover:bg-accent border border-border">
            <Link to="/QualificationLifecycleServicesPage">
              Service Details
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}